import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Button } from "@components/ui/button"
import { useInboundDropAccountMutation } from "@apis/accounts/useAccounts"
import type { IAccountInboundDropRequest } from "@apis/accounts/types"
import { Loader2 } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"
import { Textarea } from "@components/ui/textarea"
import { DatePicker } from "@components/DatePicker"
import ErrorMessage from "@components/ErrorMessage"

type TUpdateAccountStatusFormProps = {
  account_id: string
}

export const UpdateAccountStatusForm = ({
  account_id,
}: TUpdateAccountStatusFormProps) => {
  const { closeDrawer } = useDrawer()

  const inboundDropAccountMutation = useInboundDropAccountMutation()

  const form = useForm<IAccountInboundDropRequest>({
    mode: "onTouched",
    defaultValues: { service_end_date: "", requested_drop_date: "", notes: "" },
  })

  const handleSubmit = (formData: IAccountInboundDropRequest) => {
    inboundDropAccountMutation.mutate(
      {
        account_id: account_id,
        request: {
          service_end_date: formData.service_end_date,
          requested_drop_date: formData.requested_drop_date,
          notes: formData.notes,
          reason_code: "MANUAL_DROP",
          reason_desc: "Account dropped from manual request",
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully updated last date of service!")
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error updating last date of service! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="tw:space-y-6">
        <div className="tw:flex tw:flex-col tw:gap-4">
          <FormField
            control={form.control}
            name="service_end_date"
            rules={{ required: "Last Date of Service is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Date of Service</FormLabel>
                <FormControl>
                  <DatePicker field={field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="requested_drop_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Requested Drop Date (Optional)</FormLabel>
                <FormControl>
                  <DatePicker field={field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Optional..." rows={5} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <ErrorMessage
          message="Note: This will drop the account!"
          variant="destructive-secondary"
        />
        <div className="tw:flex tw:gap-6">
          <Button variant="warning" onClick={closeDrawer}>
            Cancel Change
          </Button>
          <Button
            type="submit"
            disabled={
              inboundDropAccountMutation.isPending || !form.formState.isValid
            }
          >
            {inboundDropAccountMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Last Date of Service</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
